import {EventEmitter} from '@billjs/event-emitter';
import gsap from "gsap";
import {
  BoxGeometry,
  CanvasTexture,
  ClampToEdgeWrapping,
  Clock,
  Color,
  Group,
  LinearFilter,
  LoadingManager,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  MeshPhongMaterial,
  NearestFilter,
  Object3d,
  PCFSoftShadowMap,
  PerspectiveCamera,
  PlaneBufferGeometry,
  PlaneGeometry,
  Quaternion,
  Raycaster,
  RepeatWrapping,
  Scene,
  ShaderMaterial,
  ShadowMaterial,
  SpotLight,
  TextureLoader,
  Vector2,
  Vector3,
  VideoTexture,
  WebGLRenderer
} from 'three';
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls.js';

import Creature from './Creature.js';
import GridAnimation from './GridAnimationMetaData.js';
import LetterAnimations from './LetterAnimations.js';
import LetterFlockAnimations from './LetterFlockAnimations.js';
import ParticleAnimations from './ParticleAnimations.js';
import SnowAnimation from './SnowAnimation.js';
import WebAudioManager from './WebAudioManager.js';

const videoBorder = {
  'winter' : {
    'u_thickness' : 50,
    'u_intensity' : 2,
    'u_noise' : 86,
    'u_time_factor' : .5,
    'u_thickness_zoomed' : 300
  },
  'autumn' : {
    'u_thickness' : 40,
    'u_intensity' : 10,
    'u_noise' : 26,
    'u_time_factor' : .5,
    'u_thickness_zoomed' : 300
  },
  'spring' : {
    'u_thickness' : 50,
    'u_intensity' : 10,
    'u_noise' : 15,
    'u_time_factor' : .45,
    'u_thickness_zoomed' : 200
  },
  'summer' : {
    'u_thickness' : 50,
    'u_intensity' : 10,
    'u_noise' : 9,
    'u_time_factor' : .35,
    'u_thickness_zoomed' : 200
  }
};

export default class HaikuThree extends EventEmitter {

  constructor(poem_files, poemsData) {
    super();

    this.poem_files = poem_files;
    this.poemsData = poemsData;
    this.state = "poem_selection";
    this.substate = "poem_selection";

    const poetLookup = poemsData.poets;

    // data manager will store all the poem info, texture and mesh refs
    this.data_manager = {};
    this.poemsData.poems.forEach((poem, i) => {
      this.data_manager[poem.key] = {
        poemData : poem,
        poetData : poetLookup[poem.author],
        wordTextures : {},
        wordGroups : {}
      };
      poem.word_assets.forEach((word_asset, i) => {
        let word = word_asset.word;
        this.data_manager[poem.key].wordTextures[word] = {
          'loaded' : 0,
          textures : new Array(word_asset.letterfiles.length)
        };
        this.data_manager[poem.key].wordGroups[word] = null;
      });
    });

    this.audio = new Audio();
    this.webAudio = new WebAudioManager();

    this.currentGridIndex = null;
  }

  initThreeScene() {

    console.log("Init Three Scene");

    this.groundY = this.findGroundLevel();

    this.canvas = document.createElement('canvas');
    document.body.appendChild(this.canvas);

    let w = window.innerWidth;
    let h = window.innerHeight;

    this.canvas.width = w + "px";
    this.canvas.height = h + "px";

    this.renderer = new WebGLRenderer({
      canvas : this.canvas,
      preserveDrawingBuffer : true,
      antialias : true,
      alpha : true
    });

    this.renderer.setSize(w, h);
    this.renderer.setPixelRatio(window.devicePixelRatio);

    this.scene = new Scene();

    let fov = 45;
    let aspect = w / h;
    this.camera = new PerspectiveCamera(fov, aspect, 0.01, 1000);
    this.scene.add(this.camera);
    this.camera.updateProjectionMatrix();

    this.camera.position.z = this.getNavGridDistance();

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);

    this.controls.enableDamping = true;
    this.controls.dampingFactor = .1;
    this.controls.rotateSpeed = .1;
    this.controls.zoomSpeed = .1;
    this.controls.enablePan = false;
    // this.controls.enableZoom = false;
    this.controls.enabled = false;

    this.clock = new Clock();
    this.loader = new TextureLoader();
    this.raycaster = new Raycaster();
    this.pointer = new Vector2(0, 0);

    this.letterAnimator = new LetterAnimations();
    this.particleAnimator = new ParticleAnimations();
    this.snowAnimator = new SnowAnimation();
    this.gridAnimator = new GridAnimation();
    this.letterFlockAnimator = new LetterFlockAnimations();

    this.particleAnimator.setGround(this.findGroundLevel());
    this.snowAnimator.setGround(this.findGroundLevel());

    // this.rainAnimator = new RainAnimation();
    this.aboutPos = new Vector3(0, 3, 1.25);
    this.bioPos = new Vector3(0, 0, 0);
    this.haikuInfoPos = new Vector3(0, -3, 1.25);

    this.animate();
  }

  initEventListeners() {
    window.addEventListener('resize', this.onResize.bind(this), false);

    if (this.canvas) {
      this.pointerIsDown = false;

      this.canvas.addEventListener(
          "pointermove", function(e) { this.onPointerMove(e); }.bind(this));

      this.canvas.addEventListener(
          "click", function(e) { this.onPointerDown(e); }.bind(this));

      this.canvas.addEventListener(
          "pointerdown", function(e) { this.pointerIsDown = true; }.bind(this));

      this.canvas.addEventListener(
          "pointerup", function(e) { this.onPointerUp(e); }.bind(this));
    }
  }

  //------ init of 4x4 grid and animation begin

  // preload the textures of icons
  async loadCutoutTextures() {
    // create array of papths

    let texturesArray = [];
    for (let i = 0; i < this.poemsData.poems.length; i++) {
      let key = this.poemsData.poems[i].key;
      let path = '/haiku-visualization/assets/cutouts/' +
                 this.poem_files[key].cutout + '_cutout.png';
      texturesArray[i] = await this.loader.loadAsync(path);
    }

    return texturesArray;
  }

  // create the navigation grid of icons 4x4
  async initThreeNavGrid() {

    console.log("Init Nav Grid");

    // WAIT for textures
    let textures = await this.loadCutoutTextures();

    let poemData = this.poemsData;
    this.navGridGroup = new Group();
    this.iconGroup = new Group();
    this.scene.add(this.iconGroup);
    this.scene.add(this.navGridGroup);

    this.navGrid = [];
    this.iconMeshes = [];
    this.letterTextures = {};
    this.letterMeshes = {};

    let gW = .21;

    for (let y = 0; y < 4; y++) {

      for (let x = 0; x < 4; x++) {

        let index = x + y * 4;
        let item = poemData.poems[index];
        let poetData = poemData.poets[item.author];
        if (poetData == undefined) {
          poetData = {
            'bio' : 'Bio...',
            'name' : 'Author Name',
            'source_link' : '',
            'source_name' : 'Source'
          };
        }

        let texture = textures[index];
        texture.center.set(0.5, 0.5);
        texture.magFilter = NearestFilter;

        const material = new ShaderMaterial({
          uniforms : {
            uImage : {value : texture},
            u_opacity : {value : 0.0},
            u_gray : {value : 1.0},
            u_fillPos : {value : 1.0}
          },
          fragmentShader :
              document.getElementById('fragment-shader-cutouts').textContent,
          vertexShader : document.getElementById('vertex-shader').textContent,
          transparent : true,
          depthWrite : false,
          depthTest : true
        });

        const geometry = new PlaneGeometry(gW, gW);
        let plane = new Mesh(geometry, material);
        plane.position.x = x * gW - (gW * .5 * 3);
        plane.position.y = (3 - y) * gW - (gW * .5 * 3);
        plane.position.z = -.01;
        plane.opos =
            new Vector3(plane.position.x, plane.position.y, plane.position.z);
        plane.oposy = plane.position.y;
        plane.scale.set(.8, .8, .8);
        plane.poemData = item;
        plane.poetData = poetData;
        plane.renderOrder = 1;
        this.navGridGroup.add(plane);
        this.navGrid.push(plane);

        item.word_assets.forEach(
            (word_asset, i) => { this.letterMeshes[word_asset.word] = {}; });

        const materialI = new ShaderMaterial({
          uniforms : {
            uImage : {value : texture},
            u_opacity : {value : 0.0},
            u_gray : {value : 1.0},
            u_fillPos : {value : 1.0}
          },
          fragmentShader :
              document.getElementById('fragment-shader-cutouts').textContent,
          vertexShader : document.getElementById('vertex-shader').textContent,
          transparent : true,
        });

        let icon = new Mesh(geometry, materialI);
        icon.position.copy(plane.position);
        icon.scale.copy(plane.scale);
        icon.renderOrder = 2;
        this.iconGroup.add(icon);
        this.iconMeshes.push(icon);
      }
    }

    console.log("Navigation Grid Loaded");

    this.doFinishedGridLoading();
    this.fire("grid-loaded");
  }

  doFinishedGridLoading() {
    if (this.finishedInit) {
      console.log("ERROR: already finished init. This should not repeat.");
      return;
    }

    let compare = function(a, b) {
      if (a.poemData.index < b.index) {
        return -1;
      }
      if (a.index > b.index) {
        return 1;
      }
      return 0;
    };

    this.navGrid.sort(compare);

    // fade in icons
    let tm = gsap.timeline();
    this.navGrid.forEach((item, i) => {
      item.material.uniforms.u_gray.value = 0;

      tm.to(item.material.uniforms.u_opacity,
            {value : .25, ease : 'sine.inOut', delay : 0, duration : 1.5},
            "<+=.1");
    });

    // tell ui to hide loading screen
    // this.fire('finished_loading', 'done');

    this.finishedInit = true;
  }

  showInitialGridAnimation() {
    console.log("SHOW INITIAL GRID ANIM");

    this.gridAnimator.playAnimation(this.navGrid);
  }

  //------
  async loadLettersForWord(key, word, folder, letterfiles, callback = null) {

    let texturesArray = this.data_manager[key].wordTextures[word].textures;

    // NOTE: more elegant if used a map instead of for loop
    for (let i = 0; i < letterfiles.length; i++) {
      if (texturesArray[i] == null) {
        let letterfile = letterfiles[i];
        let pathLetter = '/haiku-visualization/' + folder + '/' + letterfile;
        texturesArray[i] = await this.loader.loadAsync(pathLetter);
        let letter = word[i];
        if (letter == " " || letter == "'")
          letter = "_";
        texturesArray[i].myLetter = letter + i;
      }

      this.data_manager[key].wordTextures[word].loaded++;
    }

    return texturesArray;
  }

  //------
  getCurrentPoemData() {
    if (!this.currentGridIndex)
      return null;
    return this.data_manager[this.currentKey].poemData;
  }

  //------ animation / render control
  pauseAnimate() { this.pauseAnimation = true; }

  unpauseAnimate() {
    this.pauseAnimation = false;
    this.animate();
  }

  animate() {

    if (this.pauseAnimation) {
      return;
    }

    let dt = this.clock.getDelta();
    if (!this.t)
      this.t = 0;
    this.t += dt;
    requestAnimationFrame(function() { this.animate(); }.bind(this));

    // if (this.boid) {
    //   this.boid.update();
    // }

    this.camera.updateProjectionMatrix();
    this.renderer.render(this.scene, this.camera);
    this.controls.update();

    let vidTime = (this.video == null) ? 0 : this.video.currentTime;
    this.particleAnimator.animate(dt, vidTime);
    if (this.currentKey == "heron" || this.currentKey == "river" ||
        this.currentKey == "rain-pine") {
      this.snowAnimator.animate(dt);
    }
    // else if (this.currentKey == "rain-pine") {
    //   this.rainAnimator.animate(dt);
    // }
    // this.spotLight.position.set(this.camera.position.x,
    // this.camera.position.y,
    //                             1);
    //
    // this.spotLight.lookAt(this.controls.target);

    if (this.videoPlane) {
      if (!this.videoPlane.u_time_factor)
        this.videoPlane.u_time_factor = .25;
      this.videoPlane.material.uniforms.u_time.value =
          this.t * this.videoPlane.u_time_factor;
    }

    // if (this.navGrid && this.state == "poem_selection") {
    //
    //   this.navGrid.forEach((item, i) => {
    //     let magnitude = .05;
    //     if (!item.ran)
    //       item.ran = Math.random() * (2 * Math.PI);
    //     //         y = sin(x * 2pi / period) * amplitude + midpoint
    //     // Where:
    //     // y is the property we want to change
    //     // x is time (increasing constantly)
    //     // 2pi is a mathematical constant (it's the circumference of a circle
    //     // with radius 1) period is how long one cycle of the sine wave takes
    //     // amplitude is how high up and down the sine wave goes
    //     let offset =
    //         Math.sin((this.t + Math.cos(item.ran)) * (2 * Math.PI) / 50) *
    //         magnitude;
    //     // item.position.y = item.oposy + offset;
    //   });
    // }

    if (this.currentKey) {
      let word =
          this.data_manager[this.currentKey].poemData.word_assets[0].word;
      // t, meshes, key, word
      this.letterAnimator.animateLetters(this.t, this.letterMeshes[word],
                                         this.currentKey, word);
    }
  }

  //------ camera movements
  panCameraToPosition(pos, distance, duration, delay, controlsEnabled = true) {

    // let distance = 1;

    if (this.camera.gsapAnim)
      this.camera.gsapAnim.kill();

    this.controls.enabled = false;
    this.camera.gsapAnim = gsap.to(this.camera.position, {
      x : pos.x,
      y : pos.y,
      z : pos.z,
      ease : 'sine.inOut',
      delay : delay,
      duration : duration,
      onComplete : function() {
        this.controls.enabled = controlsEnabled;
        this.setControlsForView({"distance" : distance});
      }.bind(this)
    });

    if (this.controls.gsapAnim)
      this.controls.gsapAnim.kill();

    this.controls.gsapAnim = gsap.to(this.controls.target, {
      x : pos.x,
      y : pos.y,
      z : pos.z - distance,
      ease : 'sine.inOut',
      delay : delay,
      duration : duration
    });
  }

  moveCameraToLookAt(object, distance, duration, delay, cameraEnabled = true) {

    var helperObject = new Group()
    var target = new Vector3(); // create once an reuse it

    // if (this.substate != "zoomed_letter") {
    this.aboutPos.set(object.position.x, object.position.y + 3,
                      object.position.z + distance);
    this.bioPos.set(object.position.x - this.getBioGroupX(), object.position.y,
                    object.position.z + distance);
    this.haikuInfoPos.set(object.position.x, object.position.y - 3,
                          object.position.z + distance);

    target = object.getWorldPosition(target);
    // new Vector3().copy(object.position);
    // object.parent.localToWorld(wp); // getWorldPosition(wp);
    helperObject.position.copy(target);
    helperObject.rotation.copy(object.rotation);
    helperObject.updateMatrix(); // this line seems unnecessary
    helperObject.translateZ(distance);

    let newCamPos = new Vector3().copy(helperObject.position);

    helperObject.translateZ(-distance);
    helperObject.translateZ(-2);

    let newTargPos = new Vector3().copy(helperObject.position);

    if (this.camera.gsapAnim)
      this.camera.gsapAnim.kill();

    this.controls.enabled = false;
    this.camera.gsapAnim = gsap.to(this.camera.position, {
      x : newCamPos.x,
      y : newCamPos.y,
      z : newCamPos.z,
      ease : 'sine.inOut',
      delay : delay,
      duration : duration,
      onComplete : function() {
        this.controls.enabled = cameraEnabled;
        this.setControlsForView({"distance" : distance});
      }.bind(this)
    });

    if (this.controls.gsapAnim)
      this.controls.gsapAnim.kill();

    this.controls.gsapAnim = gsap.to(this.controls.target, {
      x : newTargPos.x,
      y : newTargPos.y,
      z : newTargPos.z,
      ease : 'sine.inOut',
      delay : delay,
      duration : duration
    });
  }

  //------- camera and object distances for screen orientations
  getNavGridDistance() {
    if (window.innerWidth < window.innerHeight) {
      return (window.innerHeight / window.innerWidth) + .25;
    }
    return 1.25;
  }

  getWordDistance(myLetterTextures) {
    let wordLength = 0;
    let sze = .25;
    myLetterTextures.forEach((texture, i) => {
      const aspectRatio = texture.image.width / texture.image.height;
      wordLength += sze * aspectRatio + .01;
    });

    if (window.innerWidth < window.innerHeight) {
      let aspect = (window.innerHeight / window.innerWidth);
      return (wordLength) * aspect + .25;
    }
    return wordLength;
  }

  findGroundLevel() {
    if (window.innerHeight > window.innerWidth) {
      return -1.45;
      if (window.innerHeight < 700) {
        return -1.45;
      }
    }
    return -1;
  }

  getVideoPosition() {

    // default positions
    let pos = new Vector3(.75, .1, 0);

    // portrait mode
    if (window.innerHeight > window.innerWidth) {
      pos.x = -.025;
      pos.y = -.35;

      let aspect = (window.innerHeight / window.innerWidth);
      aspect = Math.max(1.15, aspect);
      let distance = (1.9) * aspect;
      pos.z = (3.25 - distance);
      pos.z -= .5;
      // if (window.innerHeight < 700) {
      //
      // pos.y = -.45;
      //}

      if (window.innerHeight < 700) {
        pos.z -= .25;
        pos.y = -.45;
      }
    } else {
      let aspect = (window.innerWidth / window.innerHeight);
      if ((aspect / 2) > .75) {
        pos.z += (aspect - 1.5) * .9;
        pos.x += (aspect - 1.5) * .1;
      }
    }

    return pos;
  }

  getShadowPosition() {
    let pos = new Vector3(.75, this.groundY + .1, 0);

    if (window.innerHeight > window.innerWidth) {
      pos.x = -.025;
      pos.y = this.groundY;
      // pos.y = -.35;
      let aspect = (window.innerHeight / window.innerWidth);
      let distance = (1.9) * aspect; // + .75;
      pos.z = (3.25 - distance);

      // if (window.innerHeight < 700) {
      pos.z -= .5;
      pos.y = this.groundY;
      // }

      if (window.innerHeight < 700) {
        // pos.z -= .25;
      }
    } else {
      let aspect = (window.innerWidth / window.innerHeight);
      if ((aspect / 2) > .75) {
        pos.z += (aspect - 1.5) * .9;
        pos.x += (aspect - 1.5) * .1;
      }
    }
    return pos;
  }

  getVideoGroupDistance() { return 3.25; }

  //------- load  video for a poem
  loadPoemVideo(poem_key, doPlay = true) {

    let vidpath = '/haiku-visualization/assets/renders/' +
                  this.poem_files[poem_key].video;

    const video = document.getElementById('haiku-video');
    this.video = video;

    if (!this.vidsource) {
      this.vidsource = document.createElement('source');
      video.appendChild(this.vidsource);
    } else {
      video.pause();
    }

    this.vidsource.setAttribute('src', vidpath);
    this.vidsource.setAttribute('type', 'video/mp4');

    // reset volume
    video.volume = 1;
    video.load();
    if (doPlay)
      video.play();

    if (!this.videoPlane) {

      const texture = new VideoTexture(video);
      const material = new ShaderMaterial({
        uniforms : {
          uImage : {value : texture},
          u_resolution : {value : [ 1200.0, 720.0 ]},
          u_time : {value : 0.0},
          u_thickness : {value : 100.0},
          u_intensity : {value : 10.0},
          u_noise : {value : 15.0},
          u_opacity : {value : 0.0}
        },
        fragmentShader : document.getElementById('fragment-shader').textContent,
        vertexShader : document.getElementById('vertex-shader').textContent,
        transparent : true,
        opacity : 0,
        depthWrite : false
      });
      const vidmaterial = new MeshBasicMaterial(
          {map : texture, transparent : true, opacity : 0});
      const geometry = new PlaneGeometry(2, 1.2);

      let videoPlane = new Mesh(geometry, material);

      let testPlane = new Mesh(new PlaneGeometry(2, 1),
                               new MeshBasicMaterial({color : 0xff0000}));

      let posVec = this.getVideoPosition();
      videoPlane.position.copy(this.getVideoPosition());
      videoPlane.rotation.set(0, -.15, 0);
      this.videoPlane = videoPlane;
      videoPlane.name = "poem_video";
      videoPlane.layers.enable(1);
      // videoPlane.renderOrder = 1;
      this.videoGroup = new Group();
      this.videoGroup.position.z = -3.5;
      this.videoGroup.add(videoPlane);
      // this.videoGroup.add(testPlane);
      this.testPlane = testPlane;

      if (!this.shadow) {
        let canvas = document.createElement("canvas");
        this.shadowcontext = canvas.getContext('2d');

        this.shadowcontext.canvas.width = 256;
        this.shadowcontext.canvas.height = 256;

        this.shadowTexture = new CanvasTexture(this.shadowcontext.canvas);
        this.shadowTexture.needsUpdate = true;
        this.shadowTexture.minFilter = LinearFilter;
        this.shadowTexture.wrapS = ClampToEdgeWrapping;
        this.shadowTexture.wrapT = ClampToEdgeWrapping;

        this.shadowImage = new Image();
        this.shadowImage.onload = function() {
          this.shadowcontext.drawImage(this.shadowImage, 0, 0,
                                       this.shadowcontext.canvas.width,
                                       this.shadowcontext.canvas.height);
          this.shadowTexture.needsUpdate = true;
        }.bind(this);
        this.shadowImage.src = "/haiku-visualization/assets/images/shadow.png";

        // create plane
        this.shadowMaterial = new MeshBasicMaterial({
          map : this.shadowTexture,
          transparent : true,
          opacity : 0,
          depthWrite : true
        });

        this.shadow =
            new Mesh(new PlaneGeometry(1, 1, 1, 1), this.shadowMaterial);
        let scale = 1;
        this.shadow.scale.set(scale * 2.25, scale, scale);
        this.shadow.position.copy(this.getShadowPosition());
        this.shadow.rotation.set(-Math.PI / 2, 0, 0);
        this.shadow.name = "MeshShadow";

        if (window.innerHeight < 700 &&
            window.innerWidth < window.innerHeight) {
          this.shadow.visible = false;
        }

        // this.shadow.renderOrder = -10;
        this.videoGroup.add(this.shadow);

        this.bioGroup = new Group();
        this.bioGroup.position.set(-3, 0, -.5);
        this.scene.add(this.bioGroup);
      }

      this.scene.add(this.videoGroup);

      this.particleAnimator.init(this.videoGroup);
      this.particleAnimator.setGround(this.findGroundLevel());

    } else {
      this.videoPlane.position.copy(this.getVideoPosition());
    }

    let season = this.data_manager[this.currentKey].poemData.season;
    this.setPoemVideoUniforms(season);
  }

  setPoemVideoUniforms(season) {
    this.videoPlane.material.uniforms.u_thickness.value =
        videoBorder[season].u_thickness;
    this.videoPlane.material.uniforms.u_intensity.value =
        videoBorder[season].u_intensity;
    this.videoPlane.material.uniforms.u_noise.value =
        videoBorder[season].u_noise;
    this.videoPlane.u_time_factor = videoBorder[season].u_time_factor
  }

  tweenPoemBorderForWordView(season) {
    if (this.videoPlane.aBorder)
      this.videoPlane.aBorder.kill();
    this.videoPlane.aBorder =
        gsap.to(this.videoPlane.material.uniforms.u_thickness,
                {value : videoBorder[season].u_thickness_zoomed, duration : 2});
  }

  tweenPoemBorderForReturnToView(season) {
    if (this.videoPlane.aBorder)
      this.videoPlane.aBorder.kill();
    this.videoPlane.aBorder =
        gsap.to(this.videoPlane.material.uniforms.u_thickness,
                {value : videoBorder[season].u_thickness, duration : 2});
  }

  //--- orbit controls settings
  setControlsForView(data) {

    if (this.state == "show_poem_word") {
      this.setControlParamsForWordView(data);
    } else {
      this.setControlParamsForGridView();
    }
    // if (this.substate == "show_bio" || this.substate == "show_about") {
    //   this.setControlParamsForGridView();
    // } else if (this.state == "show_poem") {
    //   this.setControlParamsForGridView();
    // } else if (this.state == "show_poem_word") {
    //   this.setControlParamsForWordView(data);
    // } else {
    //   this.setControlParamsForGridView();
    // }
  }

  setControlParamsForWordView(data) {
    console.log("setControlParamsForWordView");
    this.controls.maxAzimuthAngle = Math.PI / 8;
    this.controls.minAzimuthAngle = -Math.PI / 8;
    this.controls.maxPolarAngle = Math.PI / 1.99;
    this.controls.minPolarAngle = Math.PI / 2.01;
    this.controls.maxDistance = Math.max(6.0, data.distance + 4);
    this.controls.minDistance = 2.5;
  }

  setControlParamsForGridView() {
    console.log("setControlParamsForGridView");
    this.controls.maxDistance = 100;
    this.controls.minDistance = -100;
    this.controls.maxAzimuthAngle = Math.PI * 2;
    this.controls.minAzimuthAngle = -Math.PI * 2;
    this.controls.maxPolarAngle = Math.PI;
    this.controls.minPolarAngle = 0;
    this.controls.enabled = false;
  }

  //------- main poem scene start

  // deep link to poem (not in use)
  async startLoadingPoemFromUrlParam(index) {
    this.state = "loading_poem";

    this.currentKey = this.navGrid[index].poemData.key;
    this.currentGridIndex = index;

    // store url fro sharing
    this.fire("set_url_for_poem", index);
    this.fire("hide_grid_view_ui", index);

    this.navGrid.forEach((item, i) => {
      gsap.to(item.material.uniforms.u_gray,
              {value : 0.0, duration : 0, overwrite : true});
      gsap.to(item.material.uniforms.u_opacity,
              {value : 0.0, duration : 0, overwrite : true});
    });

    // reset and show clone and hide selected icon
    this.iconMeshes[index].position.copy(this.navGrid[index].position);
    this.iconMeshes[index].rotation.copy(this.navGrid[index].rotation);
    this.iconMeshes[index].scale.copy(this.navGrid[index].scale);
    this.navGrid[index].material.uniforms.u_opacity.value = 0.0;
    this.iconMeshes[index].material.uniforms.u_opacity.value = .25;
    this.iconMeshes[index].position.set(this.iconMeshes[index].position.x + .01,
                                        this.iconMeshes[index].position.y + .01,
                                        this.iconMeshes[index].position.z +
                                            .01);

    this.moveCameraToLookAt(this.navGrid[index], 1, 0, 0);

    this.loadPoemVideo(this.currentKey, false);

    // show loading indicator
    // this.fire("show_poem_loading");
    this.animateIconLoadingFill(this.iconMeshes[index]);

    let time = Date.now();

    // load the letters
    let first = this.data_manager[this.currentKey].poemData.word_assets[0];
    await this.loadLettersForWord(this.currentKey, first.word, first.folder,
                                  first.letterfiles);
    let texs =
        this.data_manager[this.currentKey].wordTextures[first.word].textures;
    this.addPoemWordToScene(texs, first.word, 0);

    let sec_word = this.data_manager[this.currentKey].poemData.word_assets[1];
    await this.loadLettersForWord(this.currentKey, sec_word.word,
                                  sec_word.folder, sec_word.letterfiles);
    let letterTexturesSrc =
        this.data_manager[this.currentKey].wordTextures[sec_word.word].textures;
    this.addPoemWordToScene(letterTexturesSrc, sec_word.word, 1);

    this.substate = "textures_done";

    // check time done
    let timeDiff = Date.now() - time;

    let minTime = 4000;
    let timeout = minTime - timeDiff;

    this.substate = "loading_poem_url";
    this.fire("loading_poem_from_url_ready")
    // start showing the poem / video scene
    // this.startPoemTM =
    //     setTimeout(function() { this.startPoem(); }.bind(this), timeout);
  }

  // start loading a poem
  async startLoadingPoem(index) {

    console.log("Start Loading Poem: ", this.currentKey);

    this.state = "loading_poem";
    this.substate = "loading_poem";

    this.currentKey = this.navGrid[index].poemData.key;
    this.currentGridIndex = index;

    // store url fro sharing
    this.fire("set_url_for_poem", index);
    this.fire("hide_grid_view_ui", index);

    if (this.startPoemTM) {
      clearTimeout(this.startPoemTM);
    }

    // hide grid of icons
    this.navGrid.forEach((item, i) => {
      gsap.to(item.material.uniforms.u_gray, {
        value : 0.0,
        ease : 'sine.inOut',
        delay : 0,
        duration : 1,
        overwrite : true
      });
      if (i != index) {
        gsap.to(item.material.uniforms.u_opacity, {
          value : 0.0,
          ease : 'sine.inOut',
          delay : 0,
          duration : 2,
          overwrite : true
        });
      } else {
        gsap.to(item.material.uniforms.u_opacity, {
          value : 0.35,
          ease : 'sine.inOut',
          delay : 0,
          duration : .25,
          overwrite : true
        });
      }
    });

    // reset and show clone and hide selected icon
    this.iconMeshes[index].position.copy(this.navGrid[index].position);
    this.iconMeshes[index].rotation.copy(this.navGrid[index].rotation);
    this.iconMeshes[index].scale.copy(this.navGrid[index].scale);

    gsap.to(this.navGrid[index].material.uniforms.u_opacity,
            {value : 0.0, duration : 0, overwrite : true});

    gsap.to(this.iconMeshes[index].material.uniforms.u_opacity,
            {value : 1.0, duration : 0, overwrite : true});

    this.iconMeshes[index].aPos = gsap.to(this.iconMeshes[index].position, {
      x : this.iconMeshes[index].position.x + .01,
      y : this.iconMeshes[index].position.y + .01,
      z : this.iconMeshes[index].position.z + .01,
      ease : 'sine.inOut',
      delay : 0,
      duration : .5,
      // overwrite : true
    });

    // move camera to selected icon
    this.moveCameraToLookAt(this.navGrid[index], 1, 1, 0);

    // load video for poem
    this.loadPoemVideo(this.currentKey, false);

    // show loading indicator
    this.fire("show_poem_loading");
    this.animateIconLoadingFill(this.iconMeshes[index]);

    let time = Date.now();

    // load the letters
    let first = this.data_manager[this.currentKey].poemData.word_assets[0];
    await this.loadLettersForWord(this.currentKey, first.word, first.folder,
                                  first.letterfiles);
    let texs =
        this.data_manager[this.currentKey].wordTextures[first.word].textures;
    this.addPoemWordToScene(texs, first.word, 0);

    let sec_word = this.data_manager[this.currentKey].poemData.word_assets[1];
    await this.loadLettersForWord(this.currentKey, sec_word.word,
                                  sec_word.folder, sec_word.letterfiles);
    let letterTexturesSrc =
        this.data_manager[this.currentKey].wordTextures[sec_word.word].textures;
    this.addPoemWordToScene(letterTexturesSrc, sec_word.word, 1);

    // check time done
    let timeDiff = Date.now() - time;

    let minTime = 4000;
    let timeout = minTime - timeDiff;

    // start showing the poem / video scene
    if (this.substate == "loading_poem")
      this.startPoemTM =
          setTimeout(function() { this.startPoem(); }.bind(this), timeout);
  }

  animateIconLoadingFill(icon) {
    // icon.material.uniforms.u_fillPos.value = 0.0;
    let tm = gsap.timeline();
    tm.to(icon.material.uniforms.u_opacity,
          {value : 0.25, duration : .5, delay : 1.2, ease : "sine.inOut"});
    tm.to(icon.material.uniforms.u_fillPos, {value : 0.0, duration : 0});
    tm.to(icon.material.uniforms.u_opacity, {value : 1.0, duration : 0});
    tm.to(icon.material.uniforms.u_fillPos,
          {value : 0.5, duration : 1, ease : "sine.inOut"});
    tm.to(icon.material.uniforms.u_fillPos,
          {value : 0.8, duration : .5, ease : "sine.inOut"});
  }

  finishIconLoadingFill(icon) {
    gsap.to(
        icon.material.uniforms.u_fillPos,
        {value : 1.0, duration : .5, ease : "sine.inOut", overwrite : true});
  }

  // starts the animation into the world with video and poem
  startPoem() {
    console.log("Poem Loaded. Begin animation.");

    let index = this.currentGridIndex;

    this.fire("hide_poem_loading");
    this.finishIconLoadingFill(this.iconMeshes[index]);

    // set the poet name
    this.fire("set_by_poet", this.data_manager[this.currentKey].poetData.name);

    // set bio data
    this.fire('set_poet_bio', {
      'author' : this.data_manager[this.currentKey].poetData.name,
      'bio' : this.data_manager[this.currentKey].poetData.bio,
      'source_link' : this.data_manager[this.currentKey].poetData.source,
      'source_name' : this.data_manager[this.currentKey].poetData.source_name
    });

    let word = this.data_manager[this.currentKey].poemData.word_assets[0].word;
    let key = this.data_manager[this.currentKey].poemData.key;
    let myLetterTextures = this.data_manager[key].wordTextures[word].textures;
    myLetterTextures.forEach((texture, i) => {
      let mesh = this.letterMeshes[word][texture.myLetter];
      if (i == 1)
        this.circleLetterFocusObj = mesh;
    });

    var target = new Vector3();
    target = this.circleLetterFocusObj.parent.getWorldPosition(target);
    this.videoGroup.position.x = target.x;
    this.videoGroup.position.y = target.y;

    let vidDelay = 0;
    const video = document.getElementById('haiku-video');
    video.play();

    // show video
    // if (this.videoPlane.matAnim)
    //   this.videoPlane.matAnim.kill();
    this.videoPlane.matAnim =
        gsap.to(this.videoPlane.material.uniforms.u_opacity, {
          value : 1,
          ease : 'sine.inOut',
          delay : vidDelay,
          duration : 2,
          overwrite : true
        });
    // if (this.shadowMaterial.matAnim)
    //   this.shadowMaterial.matAnim.kill();
    if (this.currentKey != "iris" && this.currentKey != "well") {
      this.shadowMaterial.matAnim = gsap.to(this.shadowMaterial, {
        opacity : .2,
        ease : 'sine.inOut',
        delay : vidDelay,
        duration : 2,
        overwrite : true
      });
      this.shadow.visible = true;
    } else {
      this.shadow.visible = false;
    }

    if (window.innerHeight < 700 && window.innerWidth < window.innerHeight) {
      this.shadow.visible = false;
    }

    // play video
    this.startPoemVidTM = setTimeout(() => {
      this.state = "show_poem";
      this.substate = "show_poem";

      this.fire('show_meta', {
        'poemData' : this.data_manager[this.currentKey].poemData,
        'state' : this.state
      });

      // show grid button
      this.fire("show_gridnav_ui");

      this.moveCameraToLookAt(this.videoGroup, this.getVideoGroupDistance(), 4,
                              0);
      this.particleAnimator.initParticles(this.currentKey,
                                          this.video.currentTime);

      if (this.currentKey == "heron" || this.currentKey == "river") {
        this.snowAnimator.init(this.videoGroup);
        this.snowAnimator.animating = true;
      } else if (this.currentKey == "rain-pine") {
        this.snowAnimator.init(this.videoGroup, "rain");
        this.snowAnimator.animating = true;
      } else if (this.currentKey == "well") {
        this.snowAnimator.clearParticlesSmooth();
        this.snowAnimator.makePuddleMeshes(this.videoGroup);
      } else {
        this.snowAnimator.clearParticlesSmooth();
      }

      // if (this.currentKey == "rain-pine") {
      //   this.rainAnimator.init(this.videoGroup);
      //   this.rainAnimator.animating = true;
      // } else {
      //   this.rainAnimator.clearParticlesSmooth();
      // }

      this.iconMeshes[index].aOpacity =
          gsap.to(this.iconMeshes[index].material.uniforms.u_opacity,
                  {value : 0, duration : 5, delay : 1});
      this.navGrid[index].aOpacity =
          gsap.to(this.navGrid[index].material.uniforms.u_opacity,
                  {value : 0, duration : 5, delay : 1});

      // setup the poem text
      let poem_text = this.data_manager[this.currentKey].poemData.english;
      this.fire("set_haiku", this.data_manager[this.currentKey].poemData);
      this.fire("set_by_poet",
                this.data_manager[this.currentKey].poetData.name);

      if (this.currentKey == "cemetery" || this.currentKey == "love") {
        document.body.classList.add("dark");
        this.fire("reset_gradient", "dark");
      } else if (this.currentKey == "sword") {
        this.fire("reset_gradient", "sword");
      } else if (this.currentKey == "stream") {
        this.fire("reset_gradient", "earth");
      } else if (this.currentKey == "cold-flower") {
        this.fire("reset_gradient", "morning");
      }
      // else if (this.currentKey == "well") {
      //   this.fire("reset_gradient", "rainbow");
      // }
      else {
        let season = this.data_manager[this.currentKey].poemData.season;
        document.body.classList.add(season);
        this.fire("reset_gradient", season);
      }

      this.pauseAudio();
    }, vidDelay * 1000);

    this.fire("show_poem",
              {key : this.currentKey, index : this.currentGridIndex});
  }

  //---- typographis letters
  // creates the plane mesh for preloaded textures of each letter
  addPoemWordToScene(myLetterTextures, word) {
    let wordLength = 0;
    let size = .25;
    let gap = 0;

    myLetterTextures.forEach((texture, i) => {
      const aspectRatio = texture.image.width / texture.image.height;
      wordLength += size * aspectRatio + gap;
    });

    let lengthSum = 0;

    if (this.data_manager[this.currentKey].wordGroups[word] == null) {
      this.data_manager[this.currentKey].wordGroups[word] = new Group();
      this.scene.add(this.data_manager[this.currentKey].wordGroups[word]);
    }
    let wordGroup = this.data_manager[this.currentKey].wordGroups[word];

    myLetterTextures.forEach((texture, i) => {
      // only add if not alredy created
      if (!this.letterMeshes[word][texture.myLetter]) {
        const material = new MeshBasicMaterial({
          color : 0xffffff,
          map : texture,
          transparent : true,
          opacity : 0,
          depthWrite : false
        });
        // material.map.minFilter = LinearFilter;

        const aspectRatio = texture.image.width / texture.image.height;
        const geometry = new PlaneGeometry(size * aspectRatio, size);
        let plane = new Mesh(geometry, material);
        lengthSum += size * aspectRatio * .5;
        plane.position.x = lengthSum - (wordLength * .5);
        plane.position.y = 0;
        plane.position.z = 0;
        plane.oposy = plane.position.y;
        plane.name = "letter";
        // this.scene.add(plane);
        wordGroup.add(plane);
        this.letterMeshes[word][texture.myLetter] = plane;
        lengthSum += size * aspectRatio * .5 + gap;
      }
    });
  }

  //---- audio
  preLoadAudio(path) {
    // not really presloading, uses streaming
    this.webAudio.loadAndPlay(path);
  }

  pauseAudio() { this.webAudio.pause(); }

  // fadeInAudio(loop = false, vol = 1) {
  // if(this.audio.paused && newAudio.currentTime > 0 && !newAudio.ended) {
  // if(this.audio.ended)
  // this.audio.loop = loop;
  // if (this.audio.paused || this.audio.ended) {
  //   this.audio.currentTime = 0;
  //   this.audio.volume = 1;
  // this.audio.play();
  // gsap.to(this.audio, {volume : vol, duration : .5});
  //   }
  // }

  //---- interactions with typographic words
  showPoemWordByIndex(index) {
    let word =
        this.data_manager[this.currentKey].poemData.word_assets[index].word;
    this.showPoemWord(word);
  }

  // zoom to see the selected word typography
  showPoemWord(word) {
    this.currentWordShowing = word;

    console.log("Show word: ", word);

    this.state = "show_poem_word";
    this.substate = "show_poem_word";

    // let poemData = this.navGrid[this.currentGridIndex].poemData;
    let poemData = this.data_manager[this.currentKey].poemData;

    let wordIndex = 0;
    if (word != poemData.word_assets[0].word) {
      wordIndex = 1;
    }

    this.audioPath = this.data_manager[this.currentKey]
                         .poemData.word_assets[wordIndex]
                         .audio;
    // let audioPath = this.data_manager[this.currentKey]
    //                     .poemData.word_assets[wordIndex]
    //                     .audio;

    gsap.to(this.video, {volume : 0.25, duration : 1});

    let otherWord = poemData.word_assets[0].word;
    if (word == poemData.word_assets[0].word)
      otherWord = poemData.word_assets[1].word;

    // show word
    let key = this.currentKey;
    let myLetterTextures = this.data_manager[key].wordTextures[word].textures;

    myLetterTextures.forEach((texture, i) => {
      let mesh = this.letterMeshes[word][texture.myLetter];
      if (mesh) {
        mesh.layers.enable(1);
        if (mesh.matAnim)
          mesh.matAnim.kill();
        mesh.matAnim = gsap.to(
            mesh.material,
            {opacity : 1, ease : 'sine.inOut', delay : 0, duration : .5});
      }
    });

    this.letterAnimator.startTimeLineAnimation(word, this.letterMeshes[word]);

    // hide other word
    let myHideLetterTextures =
        this.data_manager[key].wordTextures[otherWord].textures;
    // this.letterTextures[otherWord];
    myHideLetterTextures.forEach((texture, i) => {
      let mesh = this.letterMeshes[otherWord][texture.myLetter];
      if (mesh) {
        if (mesh.matAnim)
          mesh.matAnim.kill();
        mesh.matAnim = gsap.to(mesh.material, {
          opacity : 0,
          ease : 'sine.inOut',
          delay : 0,
          duration : .25,
          onComplete : function() { mesh.layers.disable(1); }.bind(this)
        });
      }
    });

    let dist = this.getWordDistance(myLetterTextures);
    let wordGroup = this.data_manager[this.currentKey].wordGroups[word];

    // wordGroup.position.set(-.25, -.25, 0);
    // if (wordIndex == 0) {
    //   wordGroup.position.set(-.5, .25, 0);
    // }

    // wordGroup.position.set(this.videoGroup.position.x + 1,
    //                        this.videoGroup.position.y - .8,
    //                        this.videoGroup.position.z + .5);
    //
    // if (wordIndex == 0) {
    //   wordGroup.scale.set(2, 2, 2);
    //   wordGroup.position.set(this.videoGroup.position.x - 1,
    //                          this.videoGroup.position.y,
    //                          this.videoGroup.position.z - 1);
    //   wordGroup.rotation.y = Math.PI / 8;
    // }

    this.moveCameraToLookAt(wordGroup, dist, 3, 0);

    if (this.videoPlane.matAnim)
      this.videoPlane.matAnim.kill();
    this.videoPlane.matAnim =
        gsap.to(this.videoPlane.material.uniforms.u_opacity,
                {value : .075, ease : 'sine.inOut', delay : 0, duration : 2});

    if (this.videoPlane.posAnim)
      this.videoPlane.posAnim.kill();
    // this.videoPlane.posAnim =
    //     gsap.to(this.videoPlane.position,
    //             {y : 1, ease : 'sine.inOut', delay : 0, duration : 4});

    if (this.shadowMaterial.matAnim)
      this.shadowMaterial.matAnim.kill();
    // this.shadowMaterial.matAnim =
    //     gsap.to(this.shadowMaterial,
    //             {opacity : .01, ease : 'sine.inOut', delay : 0, duration :
    //             2});

    this.tweenPoemBorderForWordView(
        this.data_manager[this.currentKey].poemData.season);

    this.fire('hide_haiku');
    this.fire('show_poem_word', word);
    this.fire('show_meta', {
      'poemData' : this.data_manager[this.currentKey].poemData,
      'state' : this.state
    })
  }

  animatePoemWord(mesh) {
    let currentPos = mesh.position;
    let tm = gsap.timeline();
    tm.to(mesh.position, {y : currentPos.y + .01, duration : .5});
    tm.to(mesh.position, {y : currentPos.y, duration : .5});

    let tm2 = gsap.timeline();
    tm2.to(mesh.rotation, {z : -.1, duration : .25});
    tm2.to(mesh.rotation, {z : .1, duration : .25});
    tm2.to(mesh.rotation, {z : 0, duration : .25});
  }

  //----  poem facts
  // showPoemFacts() {
  //   let poemData = this.data_manager[this.currentKey].poemData;
  //   poemData.word_assets.forEach((item, i) => {
  //     if (this.letterMeshes[item.word]) {
  //       Object.values(this.letterMeshes[item.word]).forEach(val => {
  //         if (val.matAnim)
  //           val.matAnim.kill();
  //         val.matAnim =
  //             gsap.to(val.material, {opacity : 0, duration : 2, delay : 0});
  //       });
  //     }
  //   });
  //
  //   this.navGrid.forEach((item, i) => {
  //     gsap.to(item.material.uniforms.u_gray,
  //             {value : 0.0, duration : 0, overwrite : true});
  //     gsap.to(item.material.uniforms.u_opacity,
  //             {value : 0.0, duration : 0, overwrite : true});
  //     gsap.to(this.iconMeshes[i].material.uniforms.u_opacity,
  //             {value : 0.0, duration : 0, overwrite : true});
  //   });
  //
  //   this.videoPlane.matAnim =
  //       gsap.to(this.videoPlane.material.uniforms.u_opacity,
  //               {value : .05, ease : 'sine.inOut', delay : 0, duration : 2});
  //
  //   this.tweenPoemBorderForWordView(
  //       this.data_manager[this.currentKey].poemData.season);
  //
  //   this.fire("hide_haiku");
  //   this.fire("hide_bio");
  //
  //   // move camera to selected icon
  //   let index = this.currentGridIndex;
  //   this.iconMeshes[index].position.copy(this.navGrid[index].position);
  //   this.iconMeshes[index].rotation.copy(this.navGrid[index].rotation);
  //   this.iconMeshes[index].scale.copy(this.navGrid[index].scale);
  //
  //   gsap.to(this.navGrid[index].material.uniforms.u_opacity,
  //           {value : 0.0, duration : 0, overwrite : true});
  //
  //   gsap.to(this.iconMeshes[index].material.uniforms.u_opacity,
  //           {value : 1.0, duration : 0, overwrite : true});
  //
  //   this.iconMeshes[index].aPos = gsap.to(this.iconMeshes[index].position, {
  //     x : this.iconMeshes[index].position.x + .01,
  //     y : this.iconMeshes[index].position.y + .01,
  //     z : this.iconMeshes[index].position.z + .01,
  //     ease : 'sine.inOut',
  //     delay : 0,
  //     duration : .5
  //   });
  //
  //   this.moveCameraToLookAt(this.navGrid[index], 1, 1, 0);
  //   this.substate = "show_poem_facts";
  //   this.fire("show_poem_facts", 1);
  // }
  //
  // closePoemFacts() {
  //   // this.gotoVideo();
  //   let index = this.currentGridIndex;
  //   gsap.to(this.navGrid[index].material.uniforms.u_opacity,
  //           {value : 0.0, duration : 1, overwrite : true});
  //   this.fire("hide_poem_facts", 0);
  // }

  //---- interactions from user navigation
  gotoNextPoem() {

    if (this.isGoingToNewPoem == true) {
      return;
    }

    this.prevState = this.state;
    this.prevSubState = this.substate;

    // find next index
    this.currentGridIndex = (this.currentGridIndex + 1) % 16;

    this.gotoPoemFromArrow(this.currentGridIndex);
  }

  gotoPrevPoem() {

    if (this.isGoingToNewPoem == true) {
      return;
    }

    this.prevState = this.state;
    this.prevSubState = this.substate;

    // find next index
    this.currentGridIndex -= 1;
    if (this.currentGridIndex < 0) {
      this.currentGridIndex = 15;
    }

    this.gotoPoemFromArrow(this.currentGridIndex);
  }

  gotoPoemFromArrow(newIndex) {

    // where are we coming from: about, bio, word, video
    // if (this.prevState == "show_poem") {

    this.isGoingToNewPoem = true;

    this.exitPrev(this.prevState, this.prevSubState);

    // end poem
    this.endPoem();

    // set position of next poem
    this.nextTM = setTimeout(function() {
      this.isGoingToNewPoem = false;
      this.startLoadingPoem(newIndex);
    }.bind(this), 2000);
  }

  exitPrev(prev, prev_sub) {

    console.log("exitPrev", prev, prev_sub);
    if (prev == "show_poem_word") {
      this.fire("hide_poem_word");
      this.letterAnimator.endTimeLineAnimation();
    } else if (prev == "poem_selection") {
      this.fire("hide_grid_view_ui");
      this.gridAnimator.interruptAnimation();
    } else if (prev == "loading_poem") {
      if (this.startPoemTM)
        clearTimeout(this.startPoemTM);
      this.fire("hide_poem_loading");
    }

    if (prev_sub == "show_about") {
      this.closeAbout();
    }

    if (prev_sub == "show_haiku_info") {
      this.closeHaikuInfoPage();
    }
  }

  gotoVideo() {

    this.prevState = this.state;
    this.prevSubState = this.substate;

    this.exitPrev(this.prevState, this.prevSubState);

    if (this.substate == "show_bio") {
      this.leavePoetBio();
    } else if (this.substate == "show_about") {
      this.closeAbout();
      this.returnToPoemVideo(2);
    } else {
      this.returnToPoemVideo();
    }
  }

  gotoGrid() {

    this.prevState = this.state;
    this.prevSubState = this.substate;

    this.exitPrev(this.prevState, this.prevSubState);

    this.closeAbout();
    this.closePoetBio();

    this.endPoem();

    this.returnToGrid();
  }

  gotoBio() {

    this.prevState = this.state;
    this.prevSubState = this.substate;

    this.exitPrev(this.prevState, this.prevSubState);

    if (this.prevSubState == "show_bio") {
      return;
    } else if (this.prevSubState == "show_about") {
      this.showPoetBio();
      this.closeAbout();
    } else {
      this.showPoetBio();
    }
  }

  gotoAbout() {

    this.prevState = this.state;
    this.prevSubState = this.substate;

    this.exitPrev(this.prevState, this.prevSubState);

    if (this.prevState == "poem_selection" &&
        this.prevSubState == "show_haiku_info") {
      // move camera
      this.panCameraToPosition(this.aboutPos, 1, 0, 0);
    }

    // if on grid
    if (this.prevState == "poem_selection") {
      this.showAbout();
    } else if (this.prevState == "poem_selection") {
      // hide poem
      this.fire("hide_haiku");
      if (this.showHaikuTM)
        clearTimeout(this.showHaikuTM);
      this.showAbout();
    } else if (this.prevSubState == "show_bio") {
      // animate bio text out bottom direction
      document.getElementById("poet-bio-container").classList.add("fromabout");
      setTimeout(function() { this.fire("hide_bio"); }.bind(this), 100);
      this.showAbout();
    } else {
      this.showAbout();
    }
  }

  goBackFromAbout() {
    console.log("goBackFromAbout", this.prevState);
    // if (this.prevSubState == "show_bio") {
    //   this.gotoBio();
    // } else
    if (this.prevState == "show_poem") {
      this.gotoVideo();
    } else if (this.prevState == "show_poem_word") {
      this.returnToPoemWord();
      this.closeAbout();
    } else if (this.prevState == "poem_selection") {
      if (this.currentKey == null) {
        // let distance = this.getNavGridDistance();
        // this.panCameraToPosition(
        //     new Vector3(this.aboutPos.x, this.aboutPos.y - 3,
        //     this.aboutPos.z), distance, 2, 0);
        this.returnToGrid();
        this.closeAbout();
        this.fire("show_grid_view_ui", 2);
      } else {
        console.log("gotoGrid from about ");
        this.gotoGrid();
      }
    } else if (this.prevState == "loading_poem") {
      this.gotoGrid();
    }
  }

  goBackFromBio() {
    if (this.prevState == "show_poem") {
      this.gotoVideo();
    } else if (this.prevState == "show_poem_word") {
      this.returnToPoemWord();
      this.closePoetBio();
    } else {
      this.gotoVideo();
    }
  }

  gotoHaikuInfo() {
    this.prevState = this.state;
    this.prevSubState = this.substate;

    this.exitPrev(this.prevState, this.prevSubState);

    // if on grid
    if (this.prevState == "poem_selection") {
      this.showHaikuInfoPage();
    }
  }

  goBackFromHaikuInfo() {
    this.closeHaikuInfoPage();
    this.returnToGrid();
    this.fire("hide_haiku_info");
  }

  returnToPoemWord() {
    let newState = this.prevState;
    let newSubState = this.prevSubState;
    this.prevState = this.state;
    this.prevSubState = this.substate;
    this.state = newState;
    this.substate = newSubState;
    let myLetterTextures = this.data_manager[this.currentKey]
                               .wordTextures[this.currentWordShowing]
                               .textures;
    let distance = this.getWordDistance(myLetterTextures);
    let wordGroup =
        this.data_manager[this.currentKey].wordGroups[this.currentWordShowing];
    this.moveCameraToLookAt(wordGroup, distance, 2, 0);
    this.fire("show_poem_word");
  }

  // zoom back to the floating video view
  returnToPoemVideo(duration = 6) {
    console.log("Return to video view.");

    if (this.state == "show_poem" && this.substate == "show_poem") {
      return;
    }

    this.state = "show_poem";
    this.substate = "show_poem";

    if (this.showHaikuTM)
      clearTimeout(this.showHaikuTM);

    // change audio
    this.pauseAudio();
    gsap.to(this.video, {volume : 1, duration : 1});

    // show haiku poem
    this.fire('show_haiku');
    this.fire('show_meta', {
      'poemData' : this.data_manager[this.currentKey].poemData,
      'state' : this.state
    });

    // camera movement
    this.moveCameraToLookAt(this.videoGroup, this.getVideoGroupDistance(),
                            duration, 0);

    // video plane properties and position
    if (this.videoPlane.matAnim)
      this.videoPlane.matAnim.kill();
    this.videoPlane.matAnim =
        gsap.to(this.videoPlane.material.uniforms.u_opacity,
                {value : 1, ease : 'sine.inOut', delay : 0, duration : 2});

    if (this.videoPlane.posAnim)
      this.videoPlane.posAnim.kill();

    // let py = (window.innerWidth > window.innerHeight) ? .1 : -.25;
    let newPos = this.getVideoPosition();
    this.videoPlane.posAnim = gsap.to(this.videoPlane.position, {
      y : newPos.y,
      x : newPos.x,
      z : newPos.z,
      ease : 'sine.inOut',
      delay : 0,
      duration : duration
    });

    if (this.shadowMaterial.matAnim)
      this.shadowMaterial.matAnim.kill();

    this.shadowMaterial.matAnim =
        gsap.to(this.shadowMaterial,
                {opacity : .2, ease : 'sine.inOut', delay : 0, duration : 2});

    this.tweenPoemBorderForReturnToView(
        this.data_manager[this.currentKey].poemData.season);
  }

  // transitions out a poem to go to another poem or to navigation
  endPoem() {

    document.body.classList.remove("dark");
    document.body.className = "";
    this.fire("reset_gradient", "");

    // hide particles
    this.particleAnimator.clearParticlesSmooth();
    this.snowAnimator.clearParticlesSmooth();
    // this.rainAnimator.clearParticlesSmooth();

    // hide words
    let poemData = this.data_manager[this.currentKey].poemData;
    poemData.word_assets.forEach((item, i) => {
      if (this.letterMeshes[item.word]) {
        Object.values(this.letterMeshes[item.word]).forEach(val => {
          if (val.matAnim)
            val.matAnim.kill();
          val.matAnim =
              gsap.to(val.material, {opacity : 0, duration : 2, delay : 0});
        });
      }
    });

    // hide video scene
    if (this.videoPlane.matAnim)
      this.videoPlane.matAnim.kill();
    this.videoPlane.matAnim =
        gsap.to(this.videoPlane.material.uniforms.u_opacity,
                {value : 0, ease : 'sine.inOut', delay : 0, duration : 2});

    this.videoPlane.pauseTM = setTimeout(
        function() { document.getElementById("haiku-video").pause(); }, 2000);

    if (this.shadowMaterial.matAnim)
      this.shadowMaterial.matAnim.kill();
    this.shadowMaterial.matAnim =
        gsap.to(this.shadowMaterial,
                {opacity : 0, ease : 'sine.inOut', delay : 0, duration : 2});

    // hide letters
    this.fire("hide_haiku");
    this.fire("hide_bio");
    this.fire("show_meta", {'poemData' : null, 'state' : null});
  }

  // zoom back and fade in the nav grid
  returnToGrid() {
    console.log("Return to grid view.");

    let index = this.currentGridIndex;

    // pause current audio from words
    this.pauseAudio();

    // remove?
    document.body.classList.remove("dark");

    // make sure reset
    if (index != null) {
      this.iconMeshes[index].material.uniforms.u_opacity.value = 0.0;

      this.navGrid[index].position.set(this.navGrid[index].opos.x,
                                       this.navGrid[index].opos.y,
                                       this.navGrid[index].opos.z);
    }

    this.navGrid.forEach((item, i) => {
      // prob don't need these kills anymore
      if (item.animScale)
        item.animScale.kill();
      if (item.matAnim)
        item.matAnim.kill();
      if (item.posAnim)
        item.posAnim.kill();
      if (item.aOpacity)
        item.aOpacity.kill();

      item.posAnim = gsap.to(
          item.position, {z : 0, ease : 'sine.inOut', delay : 0, duration : 0});

      gsap.to(item.scale, {
        x : .8,
        y : .8,
        z : .8,
        ease : 'sine.inOut',
        delay : 0,
        duration : 0
      });

      item.matAnim = gsap.to(item.material, {
        opacity : 1,
        ease : 'sine.inOut',
        delay : 0,
        duration : 1,
        overwrite : true
      });

      gsap.to(item.material.uniforms.u_gray, {
        value : 1.0,
        ease : 'sine.inOut',
        delay : 0,
        duration : 1,
        overwrite : true
      });
      gsap.to(item.material.uniforms.u_opacity, {
        value : 1.0,
        ease : 'sine.inOut',
        delay : 0,
        duration : 1,
        overwrite : true
      });
    });

    this.moveCameraToLookAt(this.navGridGroup, this.getNavGridDistance(), 2, 0,
                            false);

    this.state = "poem_selection";
    this.substate = "poem_selection";
    this.setControlParamsForGridView();

    this.fire("show_grid_view_ui", 2);
  }

  // show bio
  getBioGroupX() {
    if (window.innerWidth > window.innerHeight) {
      let aspectRatio = window.innerWidth / window.innerHeight;
      return 3 * aspectRatio;
    } else {
      return 3;
    }
  }

  showPoetBio() {

    if (this.substate == "show_bio")
      return;

    if (this.showHaikuTM)
      clearTimeout(this.showHaikuTM);

    this.bioGroup.position.copy(this.videoGroup.position);
    this.bioGroup.translateX(-3);

    console.log("Show Poet Bio.");

    let duration = 2;
    // if (this.state == "show_poem_word")
    //   duration = 6;

    this.substate = "show_bio";

    // let offsetPos = new Vector3(this.camera.position.x - 3,
    //                             this.camera.position.y,
    //                             this.camera.position.z);
    // // this.moveCameraToLookAt(this.bioGroup, 3.25, duration, 0);
    // if (this.prevSubState == "show_about") {
    //   offsetPos.y = this.camera.position.y - 3;
    //   // if(this.)
    // }
    this.panCameraToPosition(this.bioPos, 1, duration, 0);

    this.fire("hide_haiku");
    if (this.showBioTM)
      clearTimeout(this.showBioTM);
    // this.showBioTM =

    this.fire("show_bio");
  }

  leavePoetBio() {

    console.log("Exit Poet Bio. Return to video view");

    this.substate = "show_poem";
    this.state = "show_poem";

    this.closePoetBio();

    if (this.showHaikuTM)
      clearTimeout(this.showHaikuTM);

    this.showHaikuTM =
        setTimeout(function() { this.fire("show_haiku"); }.bind(this), 2000);

    this.moveCameraToLookAt(this.videoGroup, this.getVideoGroupDistance(), 2,
                            0);

    // video plane properties and position
    if (this.videoPlane.matAnim)
      this.videoPlane.matAnim.kill();
    if (this.videoPlane.posAnim)
      this.videoPlane.posAnim.kill();

    this.videoPlane.matAnim =
        gsap.to(this.videoPlane.material.uniforms.u_opacity,
                {value : 1, ease : 'sine.inOut', delay : 0, duration : 2});

    let py = this.getVideoPosition().y;
    this.videoPlane.posAnim =
        gsap.to(this.videoPlane.position,
                {y : py, ease : 'sine.inOut', delay : 0, duration : 2});

    if (this.shadowMaterial.matAnim)
      this.shadowMaterial.matAnim.kill();

    this.shadowMaterial.matAnim =
        gsap.to(this.shadowMaterial,
                {opacity : .2, ease : 'sine.inOut', delay : 0, duration : 2});

    this.tweenPoemBorderForReturnToView(
        this.data_manager[this.currentKey].poemData.season);
  }

  closePoetBio() {
    if (this.showBioTM)
      clearTimeout(this.showBioTM);

    this.fire("hide_bio");
  }

  // about page
  showAbout() {

    this.substate = "show_about";

    if (this.showBioTM)
      clearTimeout(this.showBioTM);
    if (this.showHaikuTM)
      clearTimeout(this.showHaikuTM);

    this.fire("hide_haiku");

    let duration = 2;
    this.panCameraToPosition(this.aboutPos, 1, duration, 0);

    this.fire("show_about");
  }

  closeAbout() {
    this.fire("close_about");
    if (this.substate != "show_bio") {
      document.getElementById("poet-bio-container")
          .classList.remove("fromabout");
    } else {
      setTimeout(function() {
        document.getElementById("poet-bio-container")
            .classList.remove("fromabout");
      }, 2000);
    }
  }

  showHaikuInfoPage() {

    this.substate = "show_haiku_info";

    let duration = 2;
    this.panCameraToPosition(this.haikuInfoPos, 1, duration, 0);

    this.fire("show_haiku_info");
  }

  closeHaikuInfoPage() { this.fire("close_haiku_info"); }

  //------- user input
  onPointerMove(event) {

    document.body.style.cursor = "unset";

    let pp = {x : this.pointer.x, y : this.pointer.y};
    if (!this.prevPointer)
      this.prevPointer = {x : pp.x, y : pp.y};
    this.pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
    this.pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;

    this.raycaster.setFromCamera(this.pointer, this.camera);

    if (this.navGrid && !this.pointerIsDown && this.state == "poem_selection") {

      const intersects = this.raycaster.intersectObjects(this.navGrid);

      this.navGrid.forEach((item, i) => {
        let intersectObject = null;

        if (intersects.length > 0)
          intersectObject = intersects[0].object;

        // hover on this one
        if (intersectObject != null && intersectObject == item) {
          if (item.animationType != "hover") {
            if (item.animScale)
              item.animScale.kill();
            item.animScale = gsap.to(item.scale, {
              x : 1.1,
              y : 1.1,
              z : 1.1,
              ease : 'sine.inOut',
              delay : 0,
              duration : .5
            });
            item.animationType = "hover";
          }

        } else if (intersects.length > 0) {
          // hover on other
          if (item.animationType != "hover-other") {

            if (item.animScale)
              item.animScale.kill();
            item.animScale = gsap.to(item.scale, {
              x : .8,
              y : .8,
              z : .8,
              ease : 'sine.inOut',
              delay : 0,
              duration : .5
            });
            item.animationType = "hover-other";
          }
        } else if (item.animationType != "hover-out") {
          // hover out
          if (item.animScale)
            item.animScale.kill();
          item.animScale = gsap.to(item.scale, {
            x : .8,
            y : .8,
            z : .8,
            ease : 'sine.inOut',
            delay : 0,
            duration : .5
          });
          item.animationType = "hover-out";
        }
      });

      if (intersects.length == 0) {
        // mouse out
        document.body.style.cursor = "unset";
      } else {
        // mouse over
        document.body.style.cursor = "pointer";
      }
    }

    else if (this.pointerIsDown && this.state == "show_poem") {
      let x = this.pointer.x - this.prevPointer.x;
      let y = this.pointer.y - this.prevPointer.y;

      let aDist = (x * x + y * y);
      console.log(aDist);
      if (aDist > .0025) {
        const intersects =
            this.raycaster.intersectObjects(this.particleAnimator.userHitAreas);
        if (intersects.length > 0) {
          console.log(intersects[0]);
          this.particleAnimator.doUserDrag(intersects);
        }
        this.prevPointer = {x : pp.x, y : pp.y};
      }
    }
  }

  // now on click
  onPointerDown(event) {
    event.preventDefault();

    this.pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
    this.pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;

    this.raycaster.setFromCamera(this.pointer, this.camera);
    this.raycaster.layers.set(null);

    if (this.state == "poem_selection") {

      const intersects = this.raycaster.intersectObjects(this.navGrid);
      if (intersects.length > 0) {

        this.navGrid.forEach((item, i) => {
          if (intersects[0].object == item) {
            this.state = "load_poem";
            this.startLoadingPoem(i);
            this.gridAnimator.interruptAnimation();
            this.fire("click_poem_icon", i);

            return;
          }
        });
      }

    } else if (this.state == "show_poem_word") {

      this.raycaster.layers.set(1);
      const intersects = this.raycaster.intersectObjects(this.scene.children);
      if (intersects.length > 0) {

        if (intersects[0].object.name == "letter") {

          if (!this.substate)
            this.substate = "zoomed_out";

          if (this.lastZoomedLetter == intersects[0].object) {
            let wordGroup = this.data_manager[this.currentKey]
                                .wordGroups[this.currentWordShowing];
            this.moveCameraToLookAt(wordGroup, 1, 3, 0);
            this.substate = "zoomed_out";
          } else {
            this.substate = "zoomed_letter";
            this.moveCameraToLookAt(intersects[0].object, .5, 2, 0);
          }

          this.animatePoemWord(intersects[0].object);
          // this.fadeInAudio();
          this.webAudio.loadAndPlay('/haiku-visualization/' + this.audioPath);

          this.lastZoomedLetter = intersects[0].object;
        }

        // if (intersects[0].object.name == "poem_video") {
        //   this.exitPrev(this.state, this.substate);
        //   this.returnToPoemVideo();
        // }

        // else if (intersects[0].object.name == "letter") {
        //
        //   if (!this.substate)
        //     this.substate = "zoomed_out";
        //
        //   if (this.lastZoomedLetter == intersects[0].object) {
        //     let wordGroup = this.data_manager[this.currentKey]
        //                         .wordGroups[this.currentWordShowing];
        //     this.moveCameraToLookAt(wordGroup, 1, 3, 0);
        //     this.substate = "zoomed_out";
        //   } else {
        //     this.substate = "zoomed_letter";
        //     this.moveCameraToLookAt(intersects[0].object, .5, 2, 0);
        //   }
        //
        //   this.animatePoemWord(intersects[0].object);
        //   this.fadeInAudio();
        //
        //   this.lastZoomedLetter = intersects[0].object;
        // }
      }
    } else if (this.state == "loading_poem" &&
               this.substate == "loading_poem_url") {
      const intersects = this.raycaster.intersectObjects(this.navGrid);
      if (intersects.length > 0) {
        this.startPoem();
      }
    } else if (this.state == "show_poem") {

      const intersects =
          this.raycaster.intersectObjects(this.particleAnimator.userHitAreas);
      if (intersects.length > 0) {
        // environment animations on clicks
        this.particleAnimator.doUserClicked(intersects);
        if (this.currentKey == "heron" || this.currentKey == "river" ||
            this.currentKey == "rain-pine") {
          this.snowAnimator.toggleShow();
        } else if (this.currentKey == "wind") {
          this.letterFlockAnimator.doFlyOffAnimation(event.clientX,
                                                     event.clientY);
          this.fire("pause_letter_anim");
        } else if (this.currentKey == "stream") {

        } else if (this.currentKey == "sword") {
          if (document.body.classList.contains("sword"))
            this.fire("reset_gradient", "dark");
          else {
            this.fire("reset_gradient", "sword");
          }
        } else if (this.currentKey == "well") {
          let hits = this.particleAnimator.getHitPoints(intersects);
          if (hits.hitFloor) {
            let pt = this.particleAnimator.userHitPointFloor;
            this.snowAnimator.makePuddle(pt.x, pt.y, pt.z);
          } //
        }
      }
    }

    //

    // detect doubletap
    if (!this.lastClick)
      this.lastClick = 0;
    let date = new Date();
    let time = date.getTime();
    const time_between_taps = 500; // 200ms
    if (time - this.lastClick < time_between_taps) {
      // this.onDoubleTap(event);
    }
    this.lastClick = time;
  }

  onDoubleTap(event) {
    console.log("double tap");

    if (this.state == "show_poem_word") {
      this.raycaster.layers.set(1);
      const intersects = this.raycaster.intersectObjects(this.scene.children);

      if (intersects.length > 0) {

        if (intersects[0].object.name == "letter") {

          if (!this.substate)
            this.substate = "zoomed_out";

          if (this.lastZoomedLetter == intersects[0].object) {
            let wordGroup = this.data_manager[this.currentKey]
                                .wordGroups[this.currentWordShowing];
            this.moveCameraToLookAt(wordGroup, 1, 3, 0);
            this.substate = "zoomed_out";
          } else {
            this.substate = "zoomed_letter";
            this.moveCameraToLookAt(intersects[0].object, .5, 2, 0);
          }

          this.animatePoemWord(intersects[0].object);
          // this.fadeInAudio();
          this.webAudio.loadAndPlay('/haiku-visualization/' + this.audioPath);

          this.lastZoomedLetter = intersects[0].object;
        }
      }
    }
  }

  onPointerUp(event) {
    this.pointerIsDown = false;
    if (this.particleAnimator)
      this.particleAnimator.doUserPointerUp();
  }

  // not in use
  // checkForLetterTransparency(texture, uvx, uvy) {
  //   let canvas = document.createElement('canvas');
  //   let context = canvas.getContext("2d");
  //   canvas.width = texture.image.width;
  //   canvas.height = texture.image.height;
  //   context.drawImage(texture.image, 0, 0);
  //   const data = context.getImageData(uvx, uvy, 1, 1);
  // }

  //---------------------------- window

  onResize() {

    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);

    // reset ground
    this.groundY = this.findGroundLevel();

    // reset video and shadow
    if (this.videoPlane) {
      let pos = this.getVideoPosition();
      this.videoPlane.position.copy(pos);

      let spos = this.getShadowPosition();
      this.shadow.position.copy(spos);
    }

    if (window.innerHeight < 700 && window.innerWidth < window.innerHeight) {
      if (this.shadow)
        this.shadow.visible = false;
    }

    if (this.state == "poem_selection") {
      this.camera.position.z = this.getNavGridDistance();
    }

    if (this.particleAnimator) {
      let prevGround = this.particleAnimator.groundY;
      this.particleAnimator.setGround(this.groundY);
      this.particleAnimator.onGroundChange(prevGround);
      this.particleAnimator.updateLineMeshes();
    }
    if (this.snowAnimator)
      this.snowAnimator.setGround(this.groundY);

    if (this.gridAnimator)
      this.gridAnimator.setLandscapeSizes();
  }

  //------
  keyPress(e) {}
};
